/**
 * CalendarColorSchemes
 *
 * An internal util to help the conversion between CalendarColor types and hex values.
 * Keeps track of which colors are available to use & the number of times it is being used.
 */

import ColorScheme from 'owa-color-utils/lib/ColorScheme';
import { generateColorScheme, generateDefaultColorSchemes } from 'owa-color-utils/lib/colorUtils';
import CalendarColor from 'owa-service/lib/contract/CalendarColor';
import { findInArray } from './findInArray';
import ColorUsage from '../store/schema/ColorUsage';
import { trace } from 'owa-trace';

let defaultColorSchemes: ColorScheme[];

/**
 * The order of colors in CalendarColor enum to assign by default. If 0 is taken, assign 5, etc.
 */
const colorIndexOrder: number[] = [0, 5, 1, 3, 8, 6, 7, 2, 4];

/**
 * The map of the number of times each default color has been used. Initialized to all 0 values.
 */
let usedDefaultColors: ColorUsage[] = colorIndexOrder.map(element => {
    return { isLocked: false, count: 0 };
});

/**
 * The default color schemes
 */
export function getDefaultColorSchemes(): ColorScheme[] {
    if (!defaultColorSchemes) {
        initializeDefaultColorSchemes();
    }

    return defaultColorSchemes;
}

/**
 * The default color schemes ordered by the colorIndexOrder
 */
export function getDefaultColorSchemesOrderedByColorIndex(): ColorScheme[] {
    return colorIndexOrder.map<ColorScheme>(colorIndex => {
        return getDefaultColorSchemes()[colorIndex];
    });
}

/**
 * Initialize the default color schemes based on current theme, reset usage of automatic colors
 */
export function initializeDefaultColorSchemes() {
    defaultColorSchemes = generateDefaultColorSchemes();
    usedDefaultColors = colorIndexOrder.map(element => {
        return { isLocked: false, count: 0 };
    });
}

/**
 * Gets the color scheme for the given CalendarColor. If CalendarColor is auto, gets the next unused ColorScheme
 * from the available default schemes.
 * @param calendarColor the CalendarColor to generate a scheme for
 * @param colorsUsed the previously used colors, for automatic color picking. If undefined, will default to the general cached colors
 */
export function getColorSchemeForCalendarColor(
    calendarColor: CalendarColor,
    lockColor?: boolean
): ColorScheme {
    let usedIndex: number =
        calendarColor === undefined || calendarColor === CalendarColor.Auto
            ? calculateNextAutoColorSchemeIndex()
            : colorIndexOrder.indexOf(calendarColor);
    registerCalendarColor(usedIndex);

    if (lockColor) {
        usedDefaultColors[usedIndex].isLocked = true;
    }

    let defaultColorSchemeIndex = colorIndexOrder[usedIndex];
    let defaultColorSchemes = getDefaultColorSchemes();
    if (defaultColorSchemeIndex < 0 || defaultColorSchemeIndex >= defaultColorSchemes.length) {
        trace.warn(
            '[getColorSchemeForCalendarColor] Calculated usedIndex is outsize default color scheme bounds'
        );
        return defaultColorSchemes[defaultColorSchemes.length - 1];
    }

    return defaultColorSchemes[defaultColorSchemeIndex];
}

/**
 * Generates the color scheme for the given primary hex color value
 * @param color the primary color hex value
 */
export function getColorSchemeForHexColor(color: string): ColorScheme {
    return generateColorScheme(color);
}

/**
 * Registers a calendar color to use, so CalendarColor.Auto has a higher chance of avoiding it
 * @param color the primary color hex value or the index of the color (based on colorIndexOrder)
 */
export function registerCalendarColor(colorOrColorIndex: string | number): void {
    if (typeof colorOrColorIndex === 'number') {
        usedDefaultColors[colorOrColorIndex].count++;
    } else {
        const usedColorIndex = colorIndexOrder.indexOf(
            getCalendarColorFromCalPrimaryString(colorOrColorIndex)
        );
        usedDefaultColors[usedColorIndex].count++;
    }
}

/**
 * Releases a calendar color from use, freeing it up for future CalendarColor.Auto usage
 * @param calendarColor the color no longer being used
 */
export function releaseCalendarColor(calendarColor: CalendarColor | string): void {
    let index;
    if (typeof calendarColor === 'string') {
        index = getCalendarColorFromCalPrimaryString(calendarColor);
    } else {
        index = calendarColor;
    }

    let mappedIndex = colorIndexOrder.indexOf(index);

    usedDefaultColors[mappedIndex].count = Math.max(usedDefaultColors[mappedIndex].count - 1, 0);
    usedDefaultColors[mappedIndex].isLocked = false;
}

/**
 * Gets the CalendarColor with the given calPrimary value. Returns Automatic if none match
 * @param calPrimary the string for the calPrimary color
 */
function getCalendarColorFromCalPrimaryString(calPrimary: string): CalendarColor {
    let defaultColorEntry = findInArray(getDefaultColorSchemes(), item => {
        return item.calPrimary.toLowerCase() == calPrimary.toLowerCase();
    });

    return defaultColorEntry.index;
}

/**
 * Looks through the used default colors to see which is the least used, then
 * returns the defaultColorScheme index (retrieved from colorIndexOrder) of the color scheme
 */
function calculateNextAutoColorSchemeIndex(): number {
    let minimumValue = Number.MAX_VALUE;
    let indexOfMin = 0;
    for (let i = 0; i < usedDefaultColors.length; i++) {
        if (!usedDefaultColors[i].isLocked && usedDefaultColors[i].count < minimumValue) {
            minimumValue = usedDefaultColors[i].count;
            indexOfMin = i;
        }
    }
    return indexOfMin;
}
